import { useState } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { Box, Checkbox, Divider, FormControlLabel, Grid, IconButton, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { supportEmail } from 'appConfig';
import { RootState } from 'redux/store';
import { MONEY_CONTAINER_TYPE, getAttribute } from 'redux/slices/wallet/moneyContainerSlice';
import { findValuesByKeys } from 'utils/helpers';
import DetailItem from 'components/Staff/ItemDetail';
import Button from 'components/Common/Button';
import Colors from 'constants/colors';
import TextInput from 'components/Common/TextInput';
import Modal from 'components/Common/Modal';
import { notify } from 'components/Common/Toast';
import { tenantName } from 'config/apiConfig';

interface AddOrUpdateBankAccount {
    changeOrAddAccount: boolean;
    updateAccountInfo: boolean;
    email: string;
}

const InnerContainer = styled('div')({
    backgroundColor: 'white',
    marginBottom: 52,
    paddingTop: '4vh',
    paddingBottom: 16,
    marginTop: 12,
});

const schema = Yup.object().shape({
    changeOrAddAccount: Yup.boolean().required(),
    updateAccountInfo: Yup.boolean().required(),
    email: Yup.string().email('Please enter a valid email').required('Email is required'),
});

function ParishonerUserDetailContainer() {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const moneyContainer = useSelector((state: RootState) => state.wallet.moneyContainer);
    const defaultMerchant = useSelector((state: RootState) => state.user.defaultMerchant);
    const companyId = useSelector((state: RootState) => state.user.defaultCompanyId);
    const username = useSelector((state: RootState) => state.user.userInfo?.username);

    const { records = [] } = moneyContainer || {};
    const { name, address, city, state, zip, phone, email } = defaultMerchant || {};
    const bankAccounts = records.filter((mc) => mc.type === MONEY_CONTAINER_TYPE.BANK_ACCOUNT && mc.status === 'ACTIVE');
    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<AddOrUpdateBankAccount>({
        resolver: yupResolver(schema),
        defaultValues: {
            changeOrAddAccount: false,
            updateAccountInfo: false,
            email: '',
        },
    });

    const attributes: any = findValuesByKeys(defaultMerchant?.profileItems || [], ['TELEPHONE', 'EIN']) ?? {};
    const { TELEPHONE, EIN } = attributes;

    const routingNumber = (attributes: []) => {
        let number = '';
        attributes.forEach((attribute) => {
            if (attribute['key'] === 'ROUTING_NUMBER') {
                number = attribute['value'];
            }
        });
        return number.length > 0 ? '****' + number.slice(-4) : number;
    };

    const handleFormSubmit = (formValues: AddOrUpdateBankAccount) => {
        const { changeOrAddAccount, updateAccountInfo, email: contactInfo } = formValues;
        if (!changeOrAddAccount && !updateAccountInfo) {
            notify('Please select an option', 'error');
            return;
        }
        const company = `${name || 'N/A'} - ${companyId || 'N/A'}`;

        const lineJump = '%0D%0A';
        const subject = `Request to change ${company}'s information`;

        const body = `Request to change: ${company}:${updateAccountInfo ? `${lineJump}- Account Information` : ''}${
            changeOrAddAccount ? `${lineJump}- Bank Account` : ''
        }${lineJump}${lineJump}User requesting change: ${username}${lineJump}Tenant:  ${tenantName}${lineJump}${lineJump}Contact Info: ${contactInfo}`;

        const mailto = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
        window.location.assign(mailto);
        notify('To complete the request please send the generated email', 'success');
    };

    return (
        <InnerContainer>
            <div style={{ marginLeft: '10%', marginRight: '10%', marginTop: 16, marginBottom: '15%' }}>
                {' '}
                {/* Add consistent padding */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingLeft: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 400, my: 1 }}>Bank Account</Typography>

                    <IconButton onClick={() => setEditModalOpen(true)}>
                        <EditIcon />
                    </IconButton>
                </div>
                <Divider sx={{ backgroundColor: '#6B7280' }} />
                {bankAccounts?.length ? (
                    bankAccounts.map((ba: any) => (
                        <Box
                            key={ba.containerId}
                            sx={{ my: 3, marginBottom: '6rem' }}
                            style={{ background: Colors.tableRow, padding: '2rem' }}
                        >
                            <DetailItem labelWidth={'10%'} label="Bank Name" value={getAttribute(ba, 'attributes', 'ACCOUNT_NAME')} />
                            <DetailItem labelWidth={'10%'} label="Routing Number" value={routingNumber(ba?.attributesSecureRetrievable)} />
                            <DetailItem labelWidth={'10%'} label="Account Number" value={`******${ba?.last4AccountDigits}`} />
                        </Box>
                    ))
                ) : (
                    <Typography mb={4}>No bank accounts found.</Typography>
                )}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        paddingLeft: 6,
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 400, my: 1 }}>Billing Details</Typography>

                    <IconButton onClick={() => setEditModalOpen(true)}>
                        <EditIcon />
                    </IconButton>
                </div>
                <Divider sx={{ backgroundColor: '#6B7280' }} />
                <Grid sx={{ my: 1 }} container spacing={1} justifyContent="center">
                    {/* Left Container */}
                    <Grid item xs={12} md={6}>
                        <DetailItem labelWidth={'50%'} label="Business name" value={name || ''} />
                        <DetailItem labelWidth={'50%'} label="Phone" value={TELEPHONE || phone || ''} />
                        <DetailItem labelWidth={'50%'} label="Email" value={email || ''} />
                        <DetailItem labelWidth={'50%'} label="Type" value="Church" />
                        <DetailItem labelWidth={'50%'} label="Business ID" value={EIN || companyId || ''} />
                    </Grid>
                    {/* Right Container */}
                    <Grid item xs={12} md={6}>
                        {' '}
                        {/* Adjust the column size for a 12-column grid */}
                        <DetailItem labelWidth={'90%'} label="Address" value={address || ''} />
                        {/* <DetailItem labelWidth={'90%'} label="City" value={city || ''} />
            <DetailItem labelWidth={'90%'} label="State" value={state || ''} />
            <DetailItem labelWidth={'90%'} label="Zip" value={zip || ''} /> */}
                    </Grid>
                </Grid>
            </div>

            {/* Edit Modal */}
            <Modal isOpen={editModalOpen} modalTitle="Request to change" onClose={() => setEditModalOpen(false)}>
                <>
                    <Typography sx={{ fontSize: 14, my: 2, fontWeight: 400 }}>
                        In order to maintain a safe and effective environment for our users we require direct contact with Catholic
                        Community to make certain changes. Please choose from the following options and an Catholic Community representative
                        will reach out to you as soon as possible. <br />
                    </Typography>
                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                        <Stack alignItems="center" my={4}>
                            <Typography sx={{ textAlign: 'center', fontSize: 18, fontWeight: 400 }}>
                                What would you like help with:
                            </Typography>
                            <Controller
                                name="changeOrAddAccount"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        label="Change or add new bank account"
                                        control={
                                            <Checkbox {...field} checkedIcon={<CheckCircleIcon />} icon={<RadioButtonUncheckedIcon />} />
                                        }
                                    />
                                )}
                            />
                            <Controller
                                name="updateAccountInfo"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        label="Update account information"
                                        control={
                                            <Checkbox {...field} checkedIcon={<CheckCircleIcon />} icon={<RadioButtonUncheckedIcon />} />
                                        }
                                    />
                                )}
                            />
                        </Stack>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    width={'100%'}
                                    placeholder="Type email"
                                    icon={null}
                                    sx={{ marginBottom: 4 }}
                                    error={!!errors.email}
                                    helperText={(errors?.email as any)?.message}
                                />
                            )}
                        />
                        <Button width={'100%'} title="Send Request" type="submit" />
                    </form>
                </>
            </Modal>
        </InnerContainer>
    );
}

export default ParishonerUserDetailContainer;
